import {ValueObject} from '@s-ui/domain'

import {ConfigContext} from '../../config/context/index.js'
import {InvalidParamsSharedError} from '../../shared/Errors/InvalidParamsSharedError.js'
import {StringSharedValueObject} from '../../shared/ValueObjects/StringSharedValueObject.js'

export class VehicleSuggesterTrackingDataValueObject extends ValueObject {
  #event
  #hitOrigin
  #hitInformation

  constructor({event, hitOrigin, hitInformation}) {
    super()
    this.#event = event
    this.#hitOrigin = hitOrigin
    this.#hitInformation = hitInformation
  }

  static validate({action, pageName, selectedOption}) {
    const {VEHICLE_SUGGESTER_TRACKING_ACTIONS: ACTIONS} = ConfigContext.get('tracking')
    const {
      VEHICLE_SUGGESTER_PRICE_RANGE_PAGE_NAME,
      VEHICLE_SUGGESTER_BODY_TYPE_PAGE_NAME,
      VEHICLE_SUGGESTER_FUEL_TYPE_PAGE_NAME,
      VEHICLE_SUGGESTER_SEATS_PAGE_NAME,
      VEHICLE_SUGGESTER_MAKES_PAGE_NAME,
      VEHICLE_SUGGESTER_RESULTS_PAGE_NAME
    } = ConfigContext.get('page')

    if (!Object.values(ACTIONS).includes(action)) {
      throw InvalidParamsSharedError.create(
        `[VehicleSuggesterTrackingDataValueObject.validate] action: ${action} is not valid`
      )
    }
    if (
      ![
        VEHICLE_SUGGESTER_PRICE_RANGE_PAGE_NAME,
        VEHICLE_SUGGESTER_BODY_TYPE_PAGE_NAME,
        VEHICLE_SUGGESTER_FUEL_TYPE_PAGE_NAME,
        VEHICLE_SUGGESTER_SEATS_PAGE_NAME,
        VEHICLE_SUGGESTER_MAKES_PAGE_NAME,
        VEHICLE_SUGGESTER_RESULTS_PAGE_NAME
      ].includes(pageName)
    ) {
      throw InvalidParamsSharedError.create(
        `[VehicleSuggesterTrackingDataValueObject.validate] pageName: ${pageName} is not valid`
      )
    }

    if (action === ACTIONS.NEXT_BUTTON_CLICKED && !selectedOption) {
      throw InvalidParamsSharedError.create(
        `[VehicleSuggesterTrackingDataValueObject.validate] selectedOption is required when action is NEXT_BUTTON_CLICKED`
      )
    }
  }

  static createFromAction({action, pageName, selectedOption}) {
    VehicleSuggesterTrackingDataValueObject.validate({action, pageName, selectedOption})

    const {VEHICLE_SUGGESTER_TRACKING_ACTIONS: ACTIONS} = ConfigContext.get('tracking')
    const {
      VEHICLE_SUGGESTER_PRICE_RANGE_PAGE_NAME,
      VEHICLE_SUGGESTER_BODY_TYPE_PAGE_NAME,
      VEHICLE_SUGGESTER_FUEL_TYPE_PAGE_NAME,
      VEHICLE_SUGGESTER_SEATS_PAGE_NAME,
      VEHICLE_SUGGESTER_MAKES_PAGE_NAME,
      VEHICLE_SUGGESTER_RESULTS_PAGE_NAME
    } = ConfigContext.get('page')

    const ACTION_EVENT_MAP = {
      [ACTIONS.NEXT_BUTTON_CLICKED]: 'Next Button Success',
      [ACTIONS.BACK_BUTTON_CLICKED]: 'Back Button Clicked',
      [ACTIONS.NEXT_BUTTON_CLICKED_ERROR]: 'Next Button Error',
      [ACTIONS.DETAIL_CLICK]: 'Car Configurator Help Button Clicked',
      [ACTIONS.CONFIGURE_CLICK]: 'Car Configurator Help Button Clicked'
    }

    const VEHICLE_SUGGESTER_PAGE_NAME_HIT_ORIGIN_MAP = {
      [VEHICLE_SUGGESTER_PRICE_RANGE_PAGE_NAME]: 'car configurator - price help',
      [VEHICLE_SUGGESTER_BODY_TYPE_PAGE_NAME]: 'car configurator - body type help',
      [VEHICLE_SUGGESTER_FUEL_TYPE_PAGE_NAME]: 'car configurator - fuel type help',
      [VEHICLE_SUGGESTER_SEATS_PAGE_NAME]: 'car configurator - seats help',
      [VEHICLE_SUGGESTER_MAKES_PAGE_NAME]: 'car configurator - makes help',
      [VEHICLE_SUGGESTER_RESULTS_PAGE_NAME]: 'car configurator - results help'
    }

    const PAGE_NAME_ERROR_HIT_INFORMATION_MAP = {
      [VEHICLE_SUGGESTER_PRICE_RANGE_PAGE_NAME]: 'price picker error',
      [VEHICLE_SUGGESTER_BODY_TYPE_PAGE_NAME]: 'body type picker error',
      [VEHICLE_SUGGESTER_FUEL_TYPE_PAGE_NAME]: 'fuel type picker error',
      [VEHICLE_SUGGESTER_SEATS_PAGE_NAME]: 'seats picker error',
      [VEHICLE_SUGGESTER_MAKES_PAGE_NAME]: 'makes picker error',
      [VEHICLE_SUGGESTER_RESULTS_PAGE_NAME]: 'results page error'
    }

    const HIT_INFORMATION_ACTIONS_MAP = {
      [ACTIONS.NEXT_BUTTON_CLICKED_ERROR]: PAGE_NAME_ERROR_HIT_INFORMATION_MAP[pageName],
      [ACTIONS.DETAIL_CLICK]: 'view details',
      [ACTIONS.CONFIGURE_CLICK]: 'configure'
    }

    const HIT_ORIGIN_ACTIONS_MAP = {
      [ACTIONS.DETAIL_CLICK]: selectedOption,
      [ACTIONS.CONFIGURE_CLICK]: selectedOption
    }

    const hitOrigin = HIT_ORIGIN_ACTIONS_MAP[action] ?? VEHICLE_SUGGESTER_PAGE_NAME_HIT_ORIGIN_MAP[pageName]

    const hitInformation = HIT_INFORMATION_ACTIONS_MAP[action] ?? selectedOption

    return new VehicleSuggesterTrackingDataValueObject({
      event: StringSharedValueObject.create({value: ACTION_EVENT_MAP[action]}),
      hitOrigin: StringSharedValueObject.create({value: hitOrigin}),
      ...(Boolean(hitInformation) && {hitInformation: StringSharedValueObject.create({value: hitInformation})})
    })
  }

  get event() {
    return this.#event.value
  }

  get hitOrigin() {
    return this.#hitOrigin.value
  }

  get hitInformation() {
    return this.#hitInformation?.value
  }

  get value() {
    return {
      event: this.#event.value,
      hitOrigin: this.#hitOrigin.value,
      hitInformation: this.#hitInformation?.value
    }
  }

  toSegmentTrack() {
    return {
      event: this.#event.value,
      data: JSON.parse(
        JSON.stringify({
          hit_origin: this.#hitOrigin.value,
          ...(this.#hitInformation?.value && {hit_information: this.#hitInformation?.value})
        })
      )
    }
  }
}
